import { DataKind, GetShiftPlanOutputResponse } from "../../../reviewAndPublish/model";
import { getVolumeTrend, isActual } from "../../../../utils/midShiftActuals";
import { buildProcessPathData } from "./processPathRows";

/**
 * Client-side CSV export for the published shift plan (UTR-REFLEX-1163).
 *
 * Builds a single, labelled multi-section CSV from the fetched getShiftPlanOutput
 * data — the same content the PDF export carried (summary metrics, shift totals,
 * the projected volume curve, and the full Process Path Headcount/Hours tables),
 * but as plain comma-separated values so the report opens directly in a
 * spreadsheet. Sections are separated by a blank line, each with its own header row.
 */

export interface PlanExportMetadata {
  /** Human-readable plan label, for display only (e.g. "Operator Override Plan"). */
  planType: string;
  /** Stable flag driving label logic — set from the plan-view identity, not the
   *  display string, so wording changes to planType don't affect the export content. */
  isRccProposedView: boolean;
  version: string;
  date: string;
  shift: string;
  stationCode: string;
  /**
   * RCC_MID_SHIFT_REPLAN enabled: label each volume point and hour column Actual or Projected.
   * When off, the caller has already removed the actual hours, so the CSV is unchanged from today.
   */
  showActuals?: boolean;
}

const kindLabel = (actual: boolean): string => (actual ? "Actual" : "Projected");

const numberFormatter = new Intl.NumberFormat("en-US");

const formatNumber = (value?: number | null): string =>
  value === undefined || value === null ? "--" : numberFormatter.format(value);

// Filename: shift-plan_<FC>_<date>_<shift>_<planType>.csv, spaces/slashes stripped.
export const buildPlanCsvFilename = (meta: PlanExportMetadata): string => {
  const slug = (value: string): string =>
    (value || "unknown").trim().replace(/[^a-zA-Z0-9-]+/g, "-").replace(/^-+|-+$/g, "");
  return `shift-plan_${slug(meta.stationCode)}_${slug(meta.date)}_${slug(meta.shift)}_${slug(meta.planType)}.csv`;
};

/**
 * Escape a single CSV cell per RFC 4180: wrap in double quotes when the value
 * contains a comma, double quote, or newline, and double any embedded quotes.
 * Numbers formatted by Intl (e.g. "1,045") contain commas, so this matters.
 */
const csvCell = (value: string | number): string => {
  const text = String(value ?? "");
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const csvRow = (cells: (string | number)[]): string => cells.map(csvCell).join(",");

/**
 * Build the shift-plan CSV text from the plan output + metadata (kept separate
 * from the download side-effect so it's unit-testable).
 */
export const buildPlanCsv = (
  shiftPlanOutput: GetShiftPlanOutputResponse,
  meta: PlanExportMetadata
): string => {
  const sections: string[] = [];

  // Metadata block.
  sections.push(
    [
      csvRow(["RCC Published Shift Plan"]),
      csvRow(["Plan Type", meta.planType]),
      csvRow(["Station", meta.stationCode]),
      csvRow(["Date", meta.date]),
      csvRow(["Shift", meta.shift]),
      csvRow(["Version", meta.version]),
    ].join("\n")
  );

  const totals = shiftPlanOutput.shiftTotals;
  const primary = shiftPlanOutput.primaryGradingVolume;

  // Recommended Volume: total units across the shift = the peak of the cumulative
  // projected curve, matching the on-screen metric card (Math.max of the curve,
  // falling back to recommended.total).
  const curvePeak = (getVolumeTrend(shiftPlanOutput) ?? [])
    .map((point) => point.recommended)
    .filter((value): value is number => value != null);
  const recommendedVolume = curvePeak.length > 0
    ? Math.max(...curvePeak)
    : primary?.recommended?.total;

  // Section 1 — Summary metrics.
  sections.push(
    [
      csvRow(["Summary", "Value"]),
      csvRow(["RCC Recommended Volume", `${formatNumber(recommendedVolume)} units`]),
      csvRow(["ALPS Planned Volume", `${formatNumber(primary?.recommended?.alps)} units`]),
      csvRow(["Labor Hours Delta", `${formatNumber(totals?.showHoursNeeded?.intValue)} hrs`]),
      csvRow([
        "Labor Hours Delta %",
        // == null guards both null and undefined, matching formatNumber's treatment
        // of missing values (else Math.abs(null) would render "0.0%").
        totals?.laborHoursDeltaPercent == null
          ? "--"
          : `${Math.abs(totals.laborHoursDeltaPercent).toFixed(1)}%`,
      ]),
    ].join("\n")
  );

  // Section 2 — Shift Totals (hours breakdown).
  sections.push(
    [
      csvRow(["Shift Totals", "Hours"]),
      csvRow(["Regular Scheduled Hours", formatNumber(totals?.regularScheduledHours?.intValue)]),
      csvRow(["VET Accepted", formatNumber(totals?.vetAcceptedHours?.intValue)]),
      csvRow(["Ready Accepted", formatNumber(totals?.amazonReadyAcceptedHours?.intValue)]),
      csvRow(["VTO Accepted", formatNumber(totals?.vtoAcceptedHours?.intValue)]),
      csvRow(["Total Available Hours", formatNumber(totals?.totalAvailableHours?.intValue)]),
    ].join("\n")
  );

  // Section 3 — projected volume curve (the chart's underlying data). The heading
  // follows the plan view (Operator Override plan → its own volume).
  const volumeHeading = meta.isRccProposedView
    ? "RCC Recommended Volume"
    : "RCC Recommended Volume - Overridden";
  const projected = getVolumeTrend(shiftPlanOutput) ?? [];
  if (projected.length > 0) {
    const volumeRows = meta.showActuals
      ? [
          csvRow([volumeHeading, "Units", "Type"]),
          ...projected.map((point) =>
            csvRow([point.time, formatNumber(point.recommended), kindLabel(isActual(point))])),
        ]
      : [
          csvRow([volumeHeading, "Units"]),
          ...projected.map((point) => csvRow([point.time, formatNumber(point.recommended)])),
        ];
    sections.push(volumeRows.join("\n"));
  }

  // Section 4 — Process Path Analysis: the full Headcount and Hours tables — every
  // process-path row per time slot plus a Total rollup, matching the on-screen table.
  const summary = (shiftPlanOutput.shiftSummary ?? []).filter(
    (category) => category.planOutput && category.planOutput.length > 0
  );
  summary.forEach((category) => {
    // Shared derivation (labels, per-slot values with 0-fill, descending sort by
    // first slot, column totals) — kept identical to the on-screen table and PDF.
    const { timeSlots: slots, dataKinds, rows: dataRows, totals: columnTotals } = buildProcessPathData(category);

    const title = category.heading === "headcount"
      ? "Headcount"
      : category.heading === "hours"
        ? "Hours"
        : `${category.heading.charAt(0).toUpperCase()}${category.heading.slice(1)}`;

    const rows = dataRows.map((row) => csvRow([row.label, ...row.values.map((value) => formatNumber(value))]));
    const totalRow = csvRow(["Total", ...columnTotals.map((value) => formatNumber(value))]);

    // Time slots run across the columns, so the Actual/Projected tag is a row under the header.
    const typeRow = meta.showActuals
      ? [csvRow(["Type", ...dataKinds.map((kind) => kindLabel(kind === DataKind.ACTUAL))])]
      : [];

    sections.push([csvRow([title, ...slots]), ...typeRow, ...rows, totalRow].join("\n"));
  });

  return sections.join("\n\n") + "\n";
};

/**
 * Build and trigger a browser download of the shift-plan CSV.
 */
export const downloadPlanCsv = (
  shiftPlanOutput: GetShiftPlanOutputResponse,
  meta: PlanExportMetadata
): void => {
  const csv = buildPlanCsv(shiftPlanOutput, meta);
  // UTF-8 BOM prefix so Excel reads the file as UTF-8.
  const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = buildPlanCsvFilename(meta);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
